import AddTicketModal                        from '@/pages/Tickets/modals/AddTicketModal';
import { Alert }                             from '@/components/ui/alert';
import { Button }                            from '@/components/ui/button';
import CodeModel                             from '@/models/services/code/CodeModel';
import List                                  from '@/components/list/List';
import React                                 from 'react';
import TicketIcon                            from '@/assets/icons/TicketIcon';
import TicketsListRow                        from './TicketsListRow';
import { listVisualUrls }                    from '@/models/codeVisualUrl';
import { toaster }                           from '@/components/ui/toaster';
import usePagedCollection                    from '@widesk-core/hooks/usePagedCollection';
import { useTranslation }                    from 'react-i18next';
import { when }                              from 'mobx';
import { ModalData }                         from '@/components/modal/Modal';
import useApiCollection                      from '@widesk-core/hooks/useApiCollection';
import DeliverableTransferStatusHistoryModel from '@/models/services/shop/DeliverableTransferStatusHistoryModel';
import WebsiteEntertainmentModel             from '@/models/services/website/WebsiteEntertainmentModel';
import CodeTypePropertyValueModel            from '@/models/services/code/CodeTypePropertyValueModel';

interface ITicketsListBoxProps {
	websiteEntertainment: WebsiteEntertainmentModel;
	setModalData: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const TicketsListBox = (props: ITicketsListBoxProps) => {
	const { websiteEntertainment, setModalData, setModalIsOpen } = props;

	const [isOpenAddTicket, setIsOpenAddTicket] = React.useState(false);
	const fetchRef = React.useRef<(() => Promise<void>)>();

	const { t } = useTranslation();
	const codeCollection = usePagedCollection(CodeModel);
	const transferStatusHistoryCollection = useApiCollection(DeliverableTransferStatusHistoryModel);
	const codeTypePropertyValueCollection = useApiCollection(CodeTypePropertyValueModel);

	return (
		<>
			<List
				afterFetch={async () => {
					// Les codes Gespass n'ont pas de produit Shop : leur type de code pointe sur un
					// produit Gespass, jamais résolu ici (cf. TicketsListRow).
					await when(() => codeCollection.every(code => code.isGespass || code.codeType.source.product.isLoaded));
					await when(() => codeCollection.every(code => code.codeKind.isLoaded));

					// Les contremarques doivent remonter à leur deliverable parent,
					// seule cible d'un transfert.
					await CodeModel.loadDeliverables(codeCollection.models);

					await transferStatusHistoryCollection
						.setFilter('deliverableTransfer.deliverable', codeCollection.models.map(code => code.deliverableId).filter((id): id is NonNullable<typeof id> => Boolean(id)))
						.setSort('createdAt', 'desc')
						.list()
						.then(async coll => {
							await Promise.all([
								coll.whenIsLoaded(m => m.deliverableTransfer),
								coll.whenIsLoaded(m => m.deliverableTransferStatus),
							]);
						});

					await listVisualUrls(codeCollection.models, codeTypePropertyValueCollection);
				}}
				beforeFetch={() => {
					codeCollection.setSort('id', 'desc');
				}}
				collection={codeCollection}
				renderRow={(model) => {
					const history = model.deliverableId
							? transferStatusHistoryCollection.models.find(m => String(m.deliverableTransfer.getId('deliverable')) === String(model.deliverableId))
							: undefined;
					
					return <TicketsListRow
						code={model}
						transferStatusHistory={history}
						codeTypePropertyValueCollection={codeTypePropertyValueCollection}
						setModalData={setModalData}
						setModalIsOpen={setModalIsOpen}
					/>}
				}
				options={{
					clickable: true,
					emptyMessages: {
						title: t('pages.tickets.list.empty.title'),
						description: t('pages.tickets.list.empty.description'),
						icon: <TicketIcon size="xl" />,
					},
					filters: {
						'codeStatus.reference': 'enabled',
						'codeKind.reference': ['ticket', 'exchange'],
						'codeType.codeEntertainment.entertainmentUrn': websiteEntertainment.entertainmentUrn,
					},
					itemsMargin: '10px 0px 10px 0px',
					itemsPadding: '10px',
					padding: '0px 10px 0px 10px',
					withSeparators: true,
				}}
				getFetch={fetch => fetchRef.current = fetch}
			/>

			<Alert title={t('pages.tickets.addTicket.title')} status="info">
				<Button variant="plain" size="sm" px={0} onClick={() => setIsOpenAddTicket(true)} type="button">
					{t('pages.tickets.addTicket.link')}
				</Button>
			</Alert>

			<AddTicketModal
				isOpen={isOpenAddTicket}
				setIsOpen={setIsOpenAddTicket}
				onSuccess={() => {
					toaster.create({ title: t('pages.tickets.addTicket.form.success.title'), type: 'success' });
					fetchRef.current?.();
				}}
			/>
		</>
	)
}

export default TicketsListBox;